/**
 * 계정 생성 모달.
 * Figma「04 계정 생성」(node 12679:4092) — RDS Dialog.
 * 닫기는 「취소」·헤더 X만 허용(오버레이·ESC 비활성).
 * GENERAL은 메뉴 권한 설정, ROLE_MANAGER는 전체 WRITE(권한 UI 안내만).
 * 관리 롤 로그인 사용자는 일반만 생성 (allowManagerRole=false).
 * 메뉴 목록은 GET menu-permissions/metadata?targetRoleName= 기준.
 */
import { adminUserService } from '@/entities/admin-user/api/admin-user.api';
import { useAdminMenuPermissionsMetadata } from '@/entities/admin-user/hooks/useAdminUsers';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Field } from '@/shared/components/ui/form-field';
import { Input } from '@/shared/components/ui/input';
import {
  popupPrimaryButtonClassName,
  popupSecondaryButtonClassName,
} from '@/shared/components/ui/popup-styles';
import {
  RadioGroup,
  RadioGroupItem,
} from '@/shared/components/ui/radio-group';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { cn } from '@/shared/lib/utils';
import type { AuthRoleCode } from '@/shared/utils/authRoles';
import { formatKoreanMobilePhone } from '@/shared/utils/phoneFormat';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { Info } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import {
  mapMenuMetadataToPermissionRows,
  applyMenuPermissionChange,
  type AdminAccountRow,
  type MenuAccessLevel,
  type MenuPermissionRow,
} from './settingsAccountData';

/**
 * 유저네임 입력 — 영문·숫자·밑줄만 허용 (한글·공백·특수문자 제거).
 */
export const sanitizeUsernameInput = (value: string): string =>
  value.replace(/[^a-zA-Z0-9_]/g, '');

/** 유저네임 유효성 — 영문으로 시작, 영문/숫자/밑줄 3자 이상 */
export const isValidUsername = (value: string): boolean =>
  /^[a-zA-Z][a-zA-Z0-9_]{2,}$/.test(value.trim());

/** 생성 UI 역할 — API create는 ROLE_MANAGER | GENERAL */
export type AccountCreateRole = 'ROLE_MANAGER' | 'GENERAL';

export type AccountCreatePayload = {
  username: string;
  email: string;
  name: string;
  phone: string;
  department: string;
  role: AccountCreateRole;
  permissions: MenuPermissionRow[];
};

type AccountCreateModalProps = {
  open: boolean;
  /**
   * 관리(ROLE_MANAGER) 역할 선택 가능 여부.
   * 마스터만 true — 관리 롤 계정은 일반만 생성.
   */
  allowManagerRole?: boolean;
  /** 로컬 중복 검사용 — API 연동 시 onCheckEmail 우선 */
  existingEmails?: string[];
  /**
   * 이메일 중복 여부 조회. true면 중복.
   * 미지정 시 adminUserService.checkEmailDuplication 호출.
   */
  onCheckEmail?: (email: string) => Promise<boolean>;
  onClose: () => void;
  onCreate: (payload: AccountCreatePayload) => void | Promise<void>;
};

const INITIAL_FORM = {
  username: '',
  email: '',
  name: '',
  phone: '',
  department: '',
  role: 'GENERAL' as AccountCreateRole,
};

export const AccountCreateModal: React.FC<AccountCreateModalProps> = ({
  open,
  allowManagerRole = false,
  existingEmails = [],
  onCheckEmail,
  onClose,
  onCreate,
}) => {
  const toast = useToast();
  const [form, setForm] = useState(INITIAL_FORM);
  const [permissions, setPermissions] = useState<MenuPermissionRow[]>([]);
  const [emailChecked, setEmailChecked] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // 모달이 열릴 때만 메뉴 metadata 조회
  const {
    data: menuMetadata,
    isLoading: menuMetaLoading,
    isError: menuMetaError,
    error: menuMetaErrorValue,
    refetch: refetchMenuMetadata,
  } = useAdminMenuPermissionsMetadata(
    open,
    form.role === 'ROLE_MANAGER' ? 'ROLE_MANAGER' : 'ROLE_GENERAL',
  );

  useEffect(() => {
    if (!open) return;
    setForm(INITIAL_FORM);
    setPermissions([]);
    setEmailChecked(false);
    setCheckingEmail(false);
    setSubmitting(false);
  }, [open]);

  // 서버 메뉴 목록이 준비되면 권한 테이블을 초기화 (기본 NONE)
  useEffect(() => {
    if (!open || !menuMetadata?.menus) return;
    setPermissions(mapMenuMetadataToPermissionRows(menuMetadata.menus));
  }, [open, menuMetadata]);

  useEffect(() => {
    if (!open || !menuMetaError || !menuMetaErrorValue) return;
    handleApiError(menuMetaErrorValue, toast);
  }, [open, menuMetaError, menuMetaErrorValue, toast]);

  // 관리 롤 계정은 ROLE_MANAGER 선택 불가 — 열릴 때·권한 변경 시 일반으로 고정
  useEffect(() => {
    if (!open || allowManagerRole) return;
    setForm((prev) =>
      prev.role === 'GENERAL' ? prev : { ...prev, role: 'GENERAL' },
    );
  }, [open, allowManagerRole]);

  const updateField = <K extends keyof typeof INITIAL_FORM>(
    key: K,
    value: (typeof INITIAL_FORM)[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (key === 'email') {
      setEmailChecked(false);
    }
  };

  const isManagerRole = form.role === 'ROLE_MANAGER';
  // 관리 역할은 전체 WRITE라 metadata 목록이 비어도 생성 가능
  const menuReady =
    (isManagerRole || permissions.length > 0) &&
    !menuMetaLoading &&
    !menuMetaError;

  const canCreate = useMemo(() => {
    const username = form.username.trim();
    const email = form.email.trim();
    const name = form.name.trim();
    return (
      isValidUsername(username) &&
      email.includes('@') &&
      name.length > 0 &&
      emailChecked &&
      menuReady &&
      !checkingEmail &&
      !submitting
    );
  }, [
    form.username,
    form.email,
    form.name,
    emailChecked,
    menuReady,
    checkingEmail,
    submitting,
  ]);

  const setPermissionLevel = (id: string, level: MenuAccessLevel) => {
    setPermissions((prev) => applyMenuPermissionChange(prev, id, level));
  };

  const handleDuplicateCheck = async () => {
    const email = form.email.trim().toLowerCase();
    if (!email) {
      toast.error('이메일을 입력해주세요.');
      return;
    }
    if (!email.includes('@')) {
      toast.error('올바른 이메일 형식을 입력해주세요.');
      return;
    }

    setCheckingEmail(true);
    try {
      let duplicated: boolean;
      if (onCheckEmail) {
        duplicated = await onCheckEmail(email);
      } else if (existingEmails.length > 0) {
        duplicated = existingEmails.some(
          (item) => item.toLowerCase() === email,
        );
      } else {
        const result = await adminUserService.checkEmailDuplication(email);
        duplicated = result.duplicated;
      }

      if (duplicated) {
        setEmailChecked(false);
        toast.error('이미 사용 중인 이메일입니다.');
        return;
      }
      setEmailChecked(true);
      toast.success('사용 가능한 이메일입니다.');
    } catch (error) {
      setEmailChecked(false);
      handleApiError(error, toast);
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleSubmit = async () => {
    if (!canCreate) {
      if (!emailChecked) {
        toast.error('이메일 중복 확인을 진행해주세요.');
        return;
      }
      if (!menuReady) {
        toast.error('메뉴 권한 목록을 불러온 뒤 다시 시도해주세요.');
        return;
      }
      toast.error('필수 항목을 입력해주세요.');
      return;
    }

    const username = form.username.trim();
    const email = form.email.trim();
    const name = form.name.trim();

    if (!isValidUsername(username)) {
      toast.error('유저네임은 영문으로 시작하며 영문·숫자·밑줄만 사용할 수 있습니다.');
      return;
    }

    setSubmitting(true);
    try {
      await onCreate({
        username,
        email,
        name,
        phone: form.phone.trim(),
        department: form.department.trim(),
        role: form.role,
        permissions: isManagerRole
          ? []
          : permissions.map((row) => ({ ...row })),
      });
    } catch (error) {
      handleApiError(error, toast);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      // 오버레이·ESC는 Content에서 막고, X·취소만 onClose
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        className="flex max-h-[90vh] w-[min(1180px,calc(100vw-2rem))] max-w-[1180px] flex-col overflow-hidden"
        onInteractOutside={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
      >        <DialogHeader className="shrink-0 items-start text-left sm:text-left">
          <DialogTitle>계정 생성</DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          {/* 모바일 1열 — grid-cols-1(minmax(0,1fr))로 열이 내용 폭만큼 늘어나지 않게 한다 */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(520px,1fr)]">
            <div className="space-y-4">
              <p className="text-sm font-semibold text-ruda-text-primary">
                기본 정보
              </p>

              <Field label="유저네임" htmlFor="create-username" required>
                <Input
                  id="create-username"
                  value={form.username}
                  onChange={(e) =>
                    updateField('username', sanitizeUsernameInput(e.target.value))
                  }
                  placeholder="영문 유저네임"
                  autoComplete="off"
                  spellCheck={false}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  영문으로 시작 · 영문·숫자·밑줄만 사용 (3자 이상)
                </p>
              </Field>

              <Field label="계정 ID (이메일)" htmlFor="create-email" required>
                <div className="flex gap-2">
                  <Input
                    id="create-email"
                    value={form.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    placeholder="name@rudalink.com"
                    className="min-w-0 flex-1"
                    autoComplete="off"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="shrink-0 self-stretch"
                    disabled={
                      checkingEmail ||
                      !form.email.trim() ||
                      !form.email.includes('@')
                    }
                    onClick={() => void handleDuplicateCheck()}
                  >
                    {checkingEmail ? '확인 중…' : '중복 확인'}
                  </Button>
                </div>
                {emailChecked ? (
                  <p className="mt-1 text-xs text-ruda-success">
                    이메일 중복 확인 완료
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-muted-foreground">
                    계정 생성 전 이메일 중복 확인이 필요합니다
                  </p>
                )}
              </Field>

              <Field label="이름" htmlFor="create-name" required>
                <Input
                  id="create-name"
                  value={form.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="이름 입력"
                />
              </Field>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="연락처" htmlFor="create-phone">
                  <Input
                    id="create-phone"
                    value={form.phone}
                    onChange={(e) =>
                      updateField(
                        'phone',
                        formatKoreanMobilePhone(e.target.value),
                      )
                    }
                    placeholder="010-0000-0000"
                    inputMode="tel"
                    maxLength={13}
                  />
                </Field>
                <Field label="소속" htmlFor="create-dept">
                  <Input
                    id="create-dept"
                    value={form.department}
                    onChange={(e) => updateField('department', e.target.value)}
                    placeholder="소속 입력"
                  />
                </Field>
              </div>

              <Field label="역할" required>
                <RadioGroup
                  value={form.role}
                  onValueChange={(value) =>
                    updateField('role', value as AccountCreateRole)
                  }
                  className="gap-2"
                >
                  {allowManagerRole ? (
                    <RoleOption
                      value="ROLE_MANAGER"
                      title="관리"
                      description="전체 메뉴 + 일반 계정 관리"
                      selected={form.role === 'ROLE_MANAGER'}
                    />
                  ) : null}
                  <RoleOption
                    value="GENERAL"
                    title="일반"
                    description="우측에서 지정한 메뉴만 접근"
                    selected={form.role === 'GENERAL'}
                  />
                </RadioGroup>
                {!allowManagerRole ? (
                  <p className="mt-1 text-xs text-muted-foreground">
                    관리 역할 계정은 일반 계정만 생성할 수 있습니다
                  </p>
                ) : null}
              </Field>

              <div className="flex items-start gap-2 rounded-12 bg-ruda-warning-50 px-3.5 py-2.5 text-sm text-ruda-warning">
                <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <p>
                  생성 완료 시 임시 비밀번호가 발급되며, 화면에 1회만
                  표시됩니다.
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-sm font-semibold text-ruda-text-primary">
                메뉴 권한 *
              </p>
              {menuMetaLoading ? (
                <LoadingSpinner tip="메뉴 목록을 불러오는 중..." />
              ) : menuMetaError ? (
                <div className="space-y-3 rounded-12 border border-ruda-border-light p-4">
                  <p className="text-sm text-muted-foreground">
                    메뉴 목록을 불러오지 못했습니다.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void refetchMenuMetadata()}
                  >
                    다시 시도
                  </Button>
                </div>
              ) : isManagerRole ? (
                <div className="rounded-12 border border-ruda-border-light bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
                  관리 역할은 모든 메뉴에 편집(WRITE) 권한이 부여됩니다. 메뉴별
                  권한은 설정하지 않습니다.
                </div>
              ) : (
                <>
                  <div className="overflow-hidden rounded-12 border border-ruda-border-light">
                    <Table className="w-full table-fixed">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[46%]">메뉴</TableHead>
                          <TableHead className="w-[18%] text-center">
                            없음
                          </TableHead>
                          <TableHead className="w-[18%] text-center">
                            조회
                          </TableHead>
                          <TableHead className="w-[18%] text-center">
                            편집
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {permissions.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell
                              className={cn(
                                row.child
                                  ? 'pl-6 font-normal text-muted-foreground'
                                  : 'font-medium',
                              )}
                            >
                              {row.child ? `ㄴ  ${row.label}` : row.label}
                            </TableCell>
                            <TableCell colSpan={3} className="p-0">
                              <RadioGroup
                                value={row.level}
                                onValueChange={(value) =>
                                  setPermissionLevel(
                                    row.id,
                                    value as MenuAccessLevel,
                                  )
                                }
                                className="grid grid-cols-3 gap-0"
                              >
                                {(
                                  ['NONE', 'READ', 'WRITE'] as MenuAccessLevel[]
                                ).map((level) => (
                                  <div
                                    key={level}
                                    className="flex items-center justify-center py-2.5"
                                  >
                                    <RadioGroupItem
                                      value={level}
                                      aria-label={`${row.label} ${level}`}
                                    />
                                  </div>
                                ))}
                              </RadioGroup>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <p className="text-sm text-ruda-text-tertiary">
                    편집 권한은 조회를 포함합니다 · 하위 메뉴는 상위 설정을
                    상속합니다
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="shrink-0 sm:justify-between">
          <p className="text-sm text-ruda-text-tertiary sm:flex-1 sm:text-left">
            * 표시는 필수 입력 항목입니다
          </p>
          <div className="flex w-full gap-2 sm:w-auto sm:min-w-[280px]">
            <Button
              type="button"
              className={popupSecondaryButtonClassName}
              onClick={onClose}
              disabled={submitting}
            >
              취소
            </Button>
            <Button
              type="button"
              className={popupPrimaryButtonClassName}
              onClick={() => void handleSubmit()}
              disabled={!canCreate}
            >
              {submitting ? '생성 중…' : '계정 생성'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

type RoleOptionProps = {
  value: AccountCreateRole;
  title: string;
  description: string;
  selected: boolean;
};

const RoleOption: React.FC<RoleOptionProps> = ({
  value,
  title,
  description,
  selected,
}) => (
  <label
    className={cn(
      'flex cursor-pointer items-center gap-3 rounded-12 border px-3.5 py-3 transition-colors',
      selected
        ? 'border-primary bg-ruda-primary-50'
        : 'border-ruda-border-light bg-white',
    )}
  >
    <RadioGroupItem value={value} />
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="text-sm font-medium text-ruda-text-primary">{title}</span>
      <span className="text-sm text-ruda-text-tertiary">{description}</span>
    </span>
  </label>
);

/** 생성 payload → 목록용 AdminAccountRow (단위 테스트용 stub) */
export const buildCreatedAdminAccount = (
  payload: AccountCreatePayload,
  createdBy: string,
): AdminAccountRow => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  const uiRole: AuthRoleCode | string =
    payload.role === 'GENERAL' ? 'ROLE_GENERAL' : payload.role;
  return {
    id: `new-${Date.now()}`,
    username: payload.username,
    email: payload.email,
    name: payload.name,
    role: uiRole,
    department: payload.department || '—',
    status: 'TEMPORARY_PASSWORD',
    lastLoginAt: null,
    createdAt: `${yyyy}-${mm}-${dd}`,
    phone: payload.phone || '—',
    createdBy,
    lastLoginIp: null,
  };
};
